const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function for API calls
async function fetchAPI(endpoint, options = {}) {
  const token = localStorage.getItem('panchi-auth-store');
  const parsed = token ? JSON.parse(token) : null;
  const sessionToken = parsed?.state?.sessionToken;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}),
    ...options.headers,
  };
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail?.message || error.detail || 'Request failed');
  }
  
  return response.json();
}

export const api = {
  // Travel Plan APIs
  async generatePlan(input) {
    return fetchAPI('/generate-plan', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  
  async applyNudge(planId, nudgeType, nudgeParams = {}) {
    return fetchAPI('/apply-nudge', {
      method: 'POST',
      body: JSON.stringify({
        plan_id: planId,
        nudge_type: nudgeType,
        nudge_params: nudgeParams,
      }),
    });
  },
  
  async getSavedPlans(userId = 'default_user') {
    return fetchAPI(`/plans?user_id=${userId}`);
  },
  
  async savePlan(planId) {
    return fetchAPI(`/plans/${planId}/save`, { method: 'POST' });
  },
  
  // Auth APIs
  async login(email, password) {
    return fetchAPI(`/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
      method: 'POST',
    });
  },
  
  async signup(email, password, name) {
    return fetchAPI(`/auth/signup?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&name=${encodeURIComponent(name || '')}`, {
      method: 'POST',
    });
  },
  
  async getCurrentUser() {
    return fetchAPI('/auth/me');
  },
  
  async logout() {
    return fetchAPI('/auth/logout', { method: 'POST' });
  },
  
  async googleAuthCallback(sessionId) {
    return fetchAPI(`/auth/google/callback?session_id=${sessionId}`, { method: 'POST' });
  },
  
  // Profile APIs
  async getProfile(userId) {
    return fetchAPI(`/profile/${userId}`);
  },
  
  async updateProfile(userId, data) {
    const params = new URLSearchParams(data).toString();
    return fetchAPI(`/profile/${userId}?${params}`, { method: 'PUT' });
  },
  
  async saveTravelDNA(userId, data) {
    return fetchAPI(`/profile/${userId}/travel-dna`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getTravelDNA(userId) {
    return fetchAPI(`/profile/${userId}/travel-dna`);
  },
  
  // Transport APIs
  async getTransportRecommendations(planId, origin, destination, travelDate, transportType = 'flight') {
    return fetchAPI('/transport-recommendations', {
      method: 'POST',
      body: JSON.stringify({
        plan_id: planId,
        origin,
        destination,
        travel_date: travelDate,
        transport_type: transportType,
      }),
    });
  },
  
  // Events APIs
  async getNearbyEvents(city, country = 'India', limit = 6) {
    return fetchAPI(`/events/nearby?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&limit=${limit}`);
  },
  
  async getEventsForPlan(planId) {
    return fetchAPI(`/events/${planId}`);
  },
  
  // Saved Intent APIs
  async getSavedIntents(userId) {
    return fetchAPI(`/saved-intents/${userId}`);
  },
  
  async saveIntent(userId, intentType, title, location = '') {
    return fetchAPI(`/saved-intent?user_id=${userId}&intent_type=${intentType}&title=${encodeURIComponent(title)}&location=${encodeURIComponent(location)}`, {
      method: 'POST',
    });
  },
  
  async deleteIntent(intentId) {
    return fetchAPI(`/saved-intent/${intentId}`, { method: 'DELETE' });
  },
  
  // Notifications APIs
  async getNotifications(userId) {
    return fetchAPI(`/notifications/${userId}`);
  },
  
  async markNotificationRead(notificationId) {
    return fetchAPI(`/notifications/${notificationId}/read`, { method: 'POST' });
  },
  
  // Subscription APIs
  async getSubscriptionStatus(userId = 'default_user') {
    return fetchAPI(`/user/subscription?user_id=${userId}`);
  },
  
  async upgradeToPro(userId = 'default_user') {
    return fetchAPI(`/upgrade?user_id=${userId}`, { method: 'POST' });
  },
  
  // Rate Limit APIs
  async getRateLimit(userId) {
    return fetchAPI(`/rate-limit/${userId}`);
  },
  
  // AI Transport Recommendation
  async getAITransportRecommendation(origin, destination, travelStyle = 'comfort', budget = 'mid-range', durationDays = 3) {
    return fetchAPI(`/transport/ai-recommendation?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travel_style=${travelStyle}&budget=${budget}&duration_days=${durationDays}`);
  },
  
  // Google OAuth Token Verification
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  async verifyGoogleToken(credential) {
    return fetchAPI('/auth/google/verify-token', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  },
  
  // Feedback APIs
  async submitPlanFeedback(planId, userId, rating, comment = '') {
    return fetchAPI(`/plan/${planId}/feedback?user_id=${userId}&rating=${rating}${comment ? `&comment=${encodeURIComponent(comment)}` : ''}`, {
      method: 'POST',
    });
  },
  
  // Community APIs
  async getCommunityReviews(limit = 20) {
    return fetchAPI(`/community/reviews?limit=${limit}`);
  },
  
  async getCommunityEvents(city = '', limit = 10) {
    return fetchAPI(`/community/events?city=${encodeURIComponent(city)}&limit=${limit}`);
  },
  
  async submitReview(userId, destination, rating, comment) {
    return fetchAPI(`/community/review?user_id=${userId}&destination=${encodeURIComponent(destination)}&rating=${rating}&comment=${encodeURIComponent(comment)}`, {
      method: 'POST',
    });
  },
  
  // Admin APIs
  async verifyAdmin(username, password) {
    return fetchAPI(`/admin/verify?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`, {
      method: 'POST',
    });
  },
  
  async getAdminDashboard() {
    return fetchAPI('/admin/dashboard');
  },
  
  async getAdminUsers(limit = 50) {
    return fetchAPI(`/admin/users?limit=${limit}`);
  },
  
  async getAdminAnalytics() {
    return fetchAPI('/admin/analytics');
  },
  
  async exportData(type, format = 'csv') {
    return fetchAPI(`/admin/export/${type}?format=${format}`);
  },
  
  // Trust Score APIs
  async getTrustScore(userId) {
    return fetchAPI(`/trust-score/${userId}`);
  },
  
  // Flight Search APIs
  async searchFlights(origin, destination, departureDate, options = {}) {
    return fetchAPI('/flights/search', {
      method: 'POST',
      body: JSON.stringify({
        origin: origin.toUpperCase(),
        destination: destination.toUpperCase(),
        departure_date: departureDate,
        adults: options.adults || 1,
        children: options.children || 0,
        infants: options.infants || 0,
        return_date: options.returnDate || null,
        max_price: options.maxPrice || null,
        non_stop: options.nonStop || false,
        currency: options.currency || 'USD',
      }),
    });
  },
  
  async searchAirports(keyword) {
    return fetchAPI(`/flights/airports/search?keyword=${encodeURIComponent(keyword)}`);
  },
  
  async getFlightSearchHistory(userId, limit = 10) {
    return fetchAPI(`/flights/search-history/${userId}?limit=${limit}`);
  },
  
  // Hotel Search APIs
  async searchHotels(destination, checkIn, checkOut, options = {}) {
    return fetchAPI('/hotels/search', {
      method: 'POST',
      body: JSON.stringify({
        destination,
        check_in: checkIn,
        check_out: checkOut,
        guests: options.guests || 2,
        rooms: options.rooms || 1,
        currency: options.currency || 'USD',
      }),
    });
  },
  
  // Events APIs
  async searchEvents(options = {}) {
    const params = new URLSearchParams();
    if (options.city) params.append('city', options.city);
    if (options.country) params.append('country', options.country);
    if (options.category) params.append('category', options.category);
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    if (options.limit) params.append('limit', options.limit);
    
    return fetchAPI(`/events/search?${params.toString()}`);
  },
  
  async getTrendingEvents(limit = 10) {
    return fetchAPI(`/events/trending?limit=${limit}`);
  },
  
  async getEventsByCategory(category, limit = 10) {
    return fetchAPI(`/events/category/${category}?limit=${limit}`);
  },
  
  // Social Pulse APIs
  async getSocialPulseNudges(destination, startDate, endDate, options = {}) {
    return fetchAPI('/social-pulse/nudges', {
      method: 'POST',
      body: JSON.stringify({
        destination,
        start_date: startDate,
        end_date: endDate,
        companions: options.companions || null,
        interests: options.interests || null,
        travel_style: options.travelStyle || null,
      }),
    });
  },
  
  async recordTravelInterest(destination, eventId = null) {
    const params = new URLSearchParams();
    params.append('destination', destination);
    if (eventId) params.append('event_id', eventId);
    
    return fetchAPI(`/social-pulse/record-interest?${params.toString()}`, {
      method: 'POST',
    });
  },
  
  // Geolocation & Currency APIs
  async detectLocation() {
    return fetchAPI('/geo/detect');
  },
  
  // Regional Booking Platforms
  async getBookingPlatforms(countryCode = null) {
    const params = countryCode ? `?country_code=${countryCode}` : '';
    return fetchAPI(`/booking/platforms${params}`);
  },
  
  // Dynamic Events
  async getDynamicEvents(options = {}) {
    const params = new URLSearchParams();
    if (options.countryCode) params.append('country_code', options.countryCode);
    if (options.city) params.append('city', options.city);
    if (options.limit) params.append('limit', options.limit);
    if (options.includeSports !== undefined) params.append('include_sports', options.includeSports);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return fetchAPI(`/events/dynamic${queryString}`);
  },
  
  async getT20WorldCupFixtures() {
    return fetchAPI('/events/t20-worldcup');
  },
  
  async getCuratedGlobalEvents() {
    return fetchAPI('/events/curated-global');
  },
  
  async getPrioritizedEvents(countryCode = null) {
    const params = countryCode ? `?country_code=${countryCode}` : '';
    return fetchAPI(`/events/prioritized${params}`);
  },
  
  async getUpcomingEvents(limit = 10) {
    return fetchAPI(`/events/upcoming?limit=${limit}`);
  },

  // AI-Powered Events APIs (using Gemini)
  async getAITrendingEvents(limit = 8) {
    return fetchAPI(`/events/ai/trending?limit=${limit}`);
  },
  
  async getAIEventsByDestination(destination, limit = 5) {
    return fetchAPI(`/events/ai/destination/${encodeURIComponent(destination)}?limit=${limit}`);
  },
  
  async getAISportsEvents(limit = 10) {
    return fetchAPI(`/events/ai/sports?limit=${limit}`);
  },
  
  async getAIFestivalEvents(limit = 10) {
    return fetchAPI(`/events/ai/festivals?limit=${limit}`);
  },
  
  async refreshAIEventsCache() {
    return fetchAPI('/events/ai/refresh', { method: 'POST' });
  },

  async getAITravelWorthyEvents(limit = 12) {
    return fetchAPI(`/events/ai/travel-worthy?limit=${limit}`);
  },

  // IPL 2026 Events APIs
  async getUpcomingIPLEvents(limit = 10) {
    return fetchAPI(`/events/ipl/upcoming?limit=${limit}`);
  },
  
  async getIPLEventsByCity(city, limit = 10) {
    return fetchAPI(`/events/ipl/city/${encodeURIComponent(city)}?limit=${limit}`);
  },
  
  async getIPLTeams() {
    return fetchAPI('/events/ipl/teams');
  },
  
  async getIPLVenues() {
    return fetchAPI('/events/ipl/venues');
  },
  
  // Travel Buddy APIs
  async getBuddySettings(userId) {
    return fetchAPI(`/buddy/settings/${userId}`);
  },
  
  async updateBuddySettings(userId, settings) {
    return fetchAPI(`/buddy/settings/${userId}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  },
  
  async searchTravelBuddies(userId, options = {}) {
    return fetchAPI(`/buddy/search/${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        destination: options.destination || null,
        travel_month: options.travelMonth || null,
      }),
    });
  },
  
  async sendBuddyInvite(userId, recipientProfileId, destination, template) {
    return fetchAPI(`/buddy/invite/${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        recipient_profile_id: recipientProfileId,
        destination,
        template,
      }),
    });
  },
  
  async respondToBuddyInvite(userId, inviteId, accept) {
    return fetchAPI(`/buddy/respond/${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        invite_id: inviteId,
        accept,
      }),
    });
  },
  
  async getMyBuddyInvites(userId) {
    return fetchAPI(`/buddy/invites/${userId}`);
  },
  
  async getMyBuddyConnections(userId) {
    return fetchAPI(`/buddy/connections/${userId}`);
  },
  
  async getBuddyAdminStats() {
    return fetchAPI('/buddy/admin/stats');
  },
  
  // ==================== Community Trip Coordination APIs ====================
  
  // Community Stats
  async getCommunityStats() {
    return fetchAPI('/community/stats');
  },
  
  // Posts
  async getCommunityPosts(postType = null, destination = null, limit = 20) {
    let url = `/community/posts?limit=${limit}`;
    if (postType) url += `&post_type=${postType}`;
    if (destination) url += `&destination=${encodeURIComponent(destination)}`;
    return fetchAPI(url);
  },
  
  async createTravelInvite(data, userId = 'default_user') {
    return fetchAPI(`/community/posts/travel-invite?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async createBroadcast(data, userId = 'default_user') {
    return fetchAPI(`/community/posts/broadcast?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getMyPosts(userId) {
    return fetchAPI(`/community/posts/my/${userId}`);
  },
  
  async closePost(postId, userId = 'default_user') {
    return fetchAPI(`/community/posts/${postId}/close?user_id=${userId}`, {
      method: 'POST',
    });
  },
  
  // Join Requests
  async sendJoinRequest(data, userId = 'default_user') {
    return fetchAPI(`/community/join-request?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getJoinRequestsForPost(postId, userId = 'default_user') {
    return fetchAPI(`/community/join-requests/${postId}?user_id=${userId}`);
  },
  
  async respondToJoinRequest(data, userId = 'default_user') {
    return fetchAPI(`/community/join-request/respond?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // Trip Matches
  async getMyTripMatches(userId) {
    return fetchAPI(`/community/matches/${userId}`);
  },
  
  async leaveTripMatch(matchId, userId = 'default_user') {
    return fetchAPI(`/community/matches/${matchId}/leave?user_id=${userId}`, {
      method: 'POST',
    });
  },
  
  // Pro Chat
  async sendChatMessage(data, userId = 'default_user') {
    return fetchAPI(`/community/chat/message?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getChatMessages(matchId, userId = 'default_user', limit = 50) {
    return fetchAPI(`/community/chat/${matchId}?user_id=${userId}&limit=${limit}`);
  },
  
  // Contact Exchange
  async updateContactConsent(data, userId = 'default_user') {
    return fetchAPI(`/community/contact/consent?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getSharedContacts(matchId, userId = 'default_user') {
    return fetchAPI(`/community/contact/${matchId}?user_id=${userId}`);
  },
  
  // Pro Status
  async getProStatus(userId) {
    return fetchAPI(`/community/pro/status/${userId}`);
  },
  
  // Safety & Moderation
  async reportUser(data, userId = 'default_user') {
    return fetchAPI(`/community/report?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async blockUser(blockedId, userId = 'default_user', matchId = null) {
    let url = `/community/block/${blockedId}?user_id=${userId}`;
    if (matchId) url += `&match_id=${matchId}`;
    return fetchAPI(url, { method: 'POST' });
  },
  
  // Admin Community Stats
  async getAdminCommunityStats() {
    return fetchAPI('/community/admin/stats');
  },
  
  // ==================== Payment APIs ====================
  
  async getProPricing() {
    return fetchAPI('/payment/pricing');
  },
  
  async createRazorpayOrder(data) {
    return fetchAPI('/payment/create-order', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async verifyRazorpayPayment(data) {
    return fetchAPI('/payment/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getPaymentStatus(orderId) {
    return fetchAPI(`/payment/status/${orderId}`);
  },
  
  // ==================== Analytics Tracking APIs ====================
  
  async trackPageView(page, referrer = null) {
    const token = localStorage.getItem('panchi-auth-store');
    const parsed = token ? JSON.parse(token) : null;
    const userId = parsed?.state?.user?.user_id;
    
    return fetchAPI(`/analytics/page-view?page=${encodeURIComponent(page)}${userId ? `&user_id=${userId}` : ''}${referrer ? `&referrer=${encodeURIComponent(referrer)}` : ''}`, {
      method: 'POST',
    }).catch(() => null); // Silent fail for analytics
  },
  
  async trackEvent(eventType, eventData = {}) {
    const token = localStorage.getItem('panchi-auth-store');
    const parsed = token ? JSON.parse(token) : null;
    const userId = parsed?.state?.user?.user_id;
    
    return fetchAPI(`/analytics/event?event_type=${encodeURIComponent(eventType)}${userId ? `&user_id=${userId}` : ''}`, {
      method: 'POST',
      body: JSON.stringify(eventData),
    }).catch(() => null); // Silent fail for analytics
  },
};

export default api;
